import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { CreateUserData, UpdateUserData } from 'api/user';
import { PaginationData, UserCommon } from 'constants/interface';
import { RootState } from 'state';

const userEntityAdapter = createEntityAdapter<UserCommon>();

export interface UsersState extends EntityState<UserCommon> {
  isLoading: boolean
  remoteTotal: number
}

export const initialState: UsersState = userEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
});

export const createUserAction = createAsyncThunk(
  'users/create',
  async (payload: CreateUserData) => {
    const userData = await apiClient.user.create(payload);
    return userData;
  },
);

export const updateUserAction = createAsyncThunk(
  'users/update',
  async (payload: UpdateUserData) => {
    const userData = await apiClient.user.update(payload);
    return userData;
  },
);

export const getUsersListAction = createAsyncThunk(
  'users/list',
  async (payload: PaginationData) => {
    const response = await apiClient.user.getList(payload);

    return response;
  },
);

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    cleanUsersAction: userEntityAdapter.removeAll,
  },
  extraReducers: (builder) => {
    builder.addCase(getUsersListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getUsersListAction.fulfilled, (state, action) => {
      userEntityAdapter.removeAll(state);
      userEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getUsersListAction.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  cleanUsersAction,
} = usersSlice.actions;

export const isLoadingUsersSelector = (state: RootState):
  boolean => state.users.isLoading;

export const usersEntitiesSelector = (state: RootState):
  Dictionary<UserCommon> => state.users.entities;

export const usersIdsSelector = (state: RootState):
  EntityId[] => state.users.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.users.remoteTotal;
